import { Close, Save } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import { useState, useEffect } from "react";
import theme from "../../theme/theme";
import AsyncAutocomplete from "../common/AsyncAutocomplete";
import { fetchEmployeePage } from "../../api/EmployeeService";

// Shift presets — full day clears the 8h bar so the record counts as "present"
const PRESETS = {
  full_day: { label: "Full Day", in: "09:00", out: "18:00" },
  half_day: { label: "Half Day", in: "09:00", out: "13:00" },
};

const withTime = (date, hhmm) => {
  const [hour, minute] = hhmm.split(":").map(Number);
  return (date || dayjs()).hour(hour).minute(minute).second(0);
};

export const CreateAttendanceDialog = ({
  open,
  onClose,
  onCreate,
  loading,
  employeeId = "",
  employeeOption = null,
  date = null,
  preset = "full_day",
}) => {
  const [formData, setFormData] = useState({
    employee_id: "",
    attendance_date: null,
    time_in: null,
    time_out: null,
    notes: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    const day = date ? dayjs(date) : dayjs();
    const { in: timeIn, out: timeOut } = PRESETS[preset] ?? PRESETS.full_day;
    setFormData({
      employee_id: employeeId || "",
      attendance_date: day,
      time_in: withTime(day, timeIn),
      time_out: withTime(day, timeOut),
      notes: "",
    });
    setErrors({});
  }, [open, employeeId, date, preset]);

  // Keep the times pinned to the selected date so the payload can't straddle days
  const handleDateChange = (newDate) => {
    if (!newDate) {
      setFormData((prev) => ({ ...prev, attendance_date: null }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      attendance_date: newDate,
      time_in: prev.time_in
        ? newDate
            .hour(prev.time_in.hour())
            .minute(prev.time_in.minute())
            .second(0)
        : null,
      time_out: prev.time_out
        ? newDate
            .hour(prev.time_out.hour())
            .minute(prev.time_out.minute())
            .second(0)
        : null,
    }));
  };

  const applyPreset = (key) => {
    const { in: timeIn, out: timeOut } = PRESETS[key];
    setFormData((prev) => ({
      ...prev,
      time_in: withTime(prev.attendance_date, timeIn),
      time_out: withTime(prev.attendance_date, timeOut),
    }));
    setErrors((prev) => ({ ...prev, time_in: null, time_out: null }));
  };

  const validate = () => {
    const next = {};
    if (!formData.employee_id) next.employee_id = "Select an employee";
    if (!formData.attendance_date?.isValid?.()) next.attendance_date = "Select a date";
    if (!formData.time_in?.isValid?.()) next.time_in = "Check-in time is required";
    if (
      formData.time_in?.isValid?.() &&
      formData.time_out?.isValid?.() &&
      !formData.time_out.isAfter(formData.time_in)
    ) {
      next.time_out = "Check-out must be after check-in";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onCreate({
      employee_id: formData.employee_id,
      attendance_date: formData.attendance_date.format("YYYY-MM-DD"),
      time_in: formData.time_in.format("HH:mm:ss"),
      time_out: formData.time_out?.isValid?.()
        ? formData.time_out.format("HH:mm:ss")
        : null,
      notes: formData.notes || undefined,
    });
  };

  const activePreset = Object.entries(PRESETS).find(
    ([, p]) =>
      formData.time_in?.format("HH:mm") === p.in &&
      formData.time_out?.format("HH:mm") === p.out,
  )?.[0];

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      PaperProps={{
        sx: {
          borderRadius: theme.dialog.borderRadius,
          minWidth: theme.dialog.minWidth,
          maxWidth: "sm",
          width: "100%",
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.dialog.headerBgColor,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          fontWeight={"bold"}
          color={theme.palette.primary.main}
        >
          Add Attendance
        </Typography>
        <IconButton onClick={onClose} disabled={loading}>
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <AsyncAutocomplete
            fullWidth
            size="medium"
            label="Employee"
            placeholder="Search employee..."
            value={formData.employee_id}
            selectedOption={employeeOption}
            onChange={(id) => {
              setFormData((prev) => ({ ...prev, employee_id: id }));
              setErrors((prev) => ({ ...prev, employee_id: null }));
            }}
            fetchPage={fetchEmployeePage}
            disabled={loading}
            error={!!errors.employee_id}
            helperText={errors.employee_id}
          />
          <DatePicker
            label="Date"
            value={formData.attendance_date}
            onChange={handleDateChange}
            disabled={loading}
            maxDate={dayjs()}
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!errors.attendance_date,
                helperText: errors.attendance_date,
              },
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Quick fill:
            </Typography>
            {Object.entries(PRESETS).map(([key, p]) => (
              <Chip
                key={key}
                label={p.label}
                size="small"
                onClick={() => applyPreset(key)}
                disabled={loading}
                variant={activePreset === key ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600,
                  ...(activePreset === key && {
                    backgroundColor: theme.palette.primary.main,
                    color: "#fff",
                  }),
                }}
              />
            ))}
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TimePicker
              label="Check In Time"
              value={formData.time_in}
              onChange={(newValue) => {
                setFormData((prev) => ({ ...prev, time_in: newValue }));
                setErrors((prev) => ({ ...prev, time_in: null }));
              }}
              disabled={loading}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.time_in,
                  helperText: errors.time_in,
                },
              }}
            />
            <TimePicker
              label="Check Out Time"
              value={formData.time_out}
              onChange={(newValue) => {
                setFormData((prev) => ({ ...prev, time_out: newValue }));
                setErrors((prev) => ({ ...prev, time_out: null }));
              }}
              disabled={loading}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.time_out,
                  helperText: errors.time_out || "Leave empty for an open session",
                },
                field: { clearable: true },
              }}
            />
          </Box>
          <TextField
            label="Notes"
            multiline
            rows={2}
            fullWidth
            value={formData.notes}
            disabled={loading}
            placeholder="Manual entry by admin"
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, notes: e.target.value }))
            }
          />
        </LocalizationProvider>
      </Box>

      <Box
        sx={{
          backgroundColor: theme.dialog.headerBgColor,
          p: 2,
          display: "flex",
          justifyContent: "end",
          gap: 2,
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          disabled={loading}
          sx={{
            backgroundColor: theme.button.backgroundColor,
            color: "#000",
            borderRadius: theme.button.borderRadius,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={<Save />}
          sx={{
            backgroundColor: theme.button.editBackgroundColor,
            borderRadius: theme.button.borderRadius,
          }}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Dialog>
  );
};
